import React, { useEffect, useMemo, useCallback } from 'react';

// Material Dashboard components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

// MUI
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import TablePagination from '@mui/material/TablePagination';
import { useTheme, alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

// MD example
import DataTable from 'examples/Tables/DataTable';

import ActivityLogsFiltersBar from './ActivityLogsFilterModal';
import { reportsApi } from 'sectors/pilates/reports/api/reportsApi';
import { CircularProgress, TextField } from '@mui/material';
import MDAvatar from 'components/MDAvatar';
import MDBadge from 'components/MDBadge';
import Autocomplete from '@mui/material/Autocomplete';
// -------- helpers ----------
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function toISODateInput(d) {
  if (!d) return '';
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
function fmtTRDateTime(iso) {
  if (!iso) return '—';
  const at = new Date(iso);
  const dd = String(at.getDate()).padStart(2, '0');
  const mm = String(at.getMonth() + 1).padStart(2, '0');
  const yyyy = at.getFullYear();
  const hh = String(at.getHours()).padStart(2, '0');
  const mi = String(at.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}

const EVENT_LABELS = {
  // ===== ACTIVITY =====
  ACTIVITY_CREATED: 'Aktivite oluşturuldu',
  ACTIVITY_UPDATED: 'Aktivite güncellendi',
  ACTIVITY_ACTIVE_CHANGED: 'Aktivite durumu değişti',
  ACTIVITY_DEACTIVATED: 'Aktivite pasifleştirildi',
  ACTIVITY_ACTIVATED: 'Aktivite aktifleştirildi',

  // ===== MEMBER =====
  MEMBER_CREATED: 'Üye oluşturuldu',
  MEMBER_UPDATED: 'Üye güncellendi',
  MEMBER_STATUS_CHANGED: 'Üye durumu değişti',
  MEMBER_INACTIVE_BY_DEBT: 'Üye borç nedeniyle pasif',
  MEMBER_WARNING: 'Üye uyarısı',

  // ===== PACKAGE =====
  PACKAGE_CREATED: 'Paket oluşturuldu',
  PACKAGE_UPDATED: 'Paket güncellendi',
  PACKAGE_RENEWED: 'Paket yenilendi',
  PACKAGE_ACTIVATED: 'Paket aktifleştirildi',
  PACKAGE_FINISHED: 'Paket manuel bitirildi',
  PACKAGE_DELETED: 'Paket silindi',
  EARLY_TRANSFER_DONE: 'Erken transfer yapıldı',

  PACKAGE_ENDED: 'Paket bitti',
  PACKAGE_ENDING_SOON: 'Paket bitmek üzere',
  PACKAGE_OVERUSED: 'Paket aşımı',
  PACKAGE_AUTO_SWITCHED: 'Paket otomatik değişti',
  PACKAGE_SWITCHED_MANUAL: 'Paket manuel değiştirildi',
  PACKAGE_USAGE_CHANGED: 'Paket kullanım değişti',

  // ===== ATTENDANCE =====
  ATTENDANCE_MARKED: 'Yoklama alındı',
  ATTENDANCE_CANCELLED: 'Yoklama iptal edildi',
  ATTENDANCE_NO_SHOW: 'Gelmedi',
  ATTENDANCE_MAKEUP_USED: 'Telafi kullanıldı',
  ATTENDANCE_UPDATED: 'Yoklama güncellendi',
  MAKEUP_CREDIT_CREATED: 'Telafi hakkı oluşturuldu',
  MAKEUP_CREDIT_CANCELLED: 'Telafi hakkı iptal edildi',

  // ===== PLAN =====
  PLAN_CREATED: 'Plan oluşturuldu',
  PLAN_UPDATED: 'Plan güncellendi',
  PLAN_ACTIVE_CHANGED: 'Plan aktifliği değişti',
  MEMBER_PLAN_STARTED: 'Üye planı başladı',
  MEMBER_PLAN_ENDED: 'Üye planı bitti',

  // ===== PAYMENT =====
  PAYMENT_CREATED: 'Ödeme oluşturuldu',
  PAYMENT_APPROVED: 'Ödeme onaylandı',
  PAYMENT_REJECTED: 'Ödeme reddedildi',
  PAYMENT_UPDATED: 'Ödeme güncellendi',
  PAYMENT_DELETED: 'Ödeme silindi',

  // ===== SESSION =====
  SESSION_CREATED: 'Seans oluşturuldu',
  SESSION_UPDATED: 'Seans güncellendi',
  SESSION_DELETED: 'Seans silindi',
  SESSION_MOVED: 'Seans taşındı',
  SESSION_COPIED: 'Seans kopyalandı',
  SESSION_WEEK_COPIED: 'Haftalık seans kopyalandı',
  SESSION_PAYMENT_RECEIVED: 'Seans ödemesi alındı',
  SESSION_NOTE_CREATED: 'Seans notu eklendi',
  SESSION_NOTE_ADDED: 'Seans notu eklendi',
  SESSION_NOTE_DELETED: 'Seans notu silindi',
  SESSION_REMINDER_CREATED: 'Seans hatırlatma oluşturuldu',
  SESSION_REMINDER_DELETED: 'Seans hatırlatma silindi',

  // ===== DEBT / ALERTS =====
  DEBT_CREATED: 'Borç oluşturuldu',
  DEBT_ADDED: 'Borç eklendi',
  DEBT_REDUCED: 'Borç azaltıldı',
  DEBT_UPDATED: 'Borç güncellendi',
  DEBT_DELETED: 'Borç silindi',
  DEBT_CLEARED: 'Borç kapatıldı',

  PAYMENT_REQUIRED: 'Ödeme gerekli',
  PAYMENT_DELAYED: 'Ödeme gecikti',

  // ===== STAFF =====
  STAFF_CREATED: 'Personel oluşturuldu',
  STAFF_UPDATED: 'Personel güncellendi',
  STAFF_STATUS_CHANGED: 'Personel durumu değişti',
  STAFF_PERMISSIONS_UPDATED: 'Personel yetkileri güncellendi',
  STAFF_LOGIN_CREATED: 'Personel girişi oluşturuldu',
  STAFF_LOGIN_UPDATED: 'Personel girişi güncellendi',
  STAFF_LOGIN_STATUS_CHANGED: 'Personel giriş durumu değişti',
};

const eventLabel = (v) => EVENT_LABELS[v] || v || '—';

function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || '';
  const b = parts[1]?.[0] || '';
  return (a + b).toUpperCase() || 'U';
}

function chipTone(type) {
  if (!type) return 'default';
  if (String(type).startsWith('PAYMENT')) return 'success';
  if (String(type).startsWith('PLAN')) return 'info';
  if (String(type).startsWith('ATTENDANCE')) return 'warning';
  if (String(type).startsWith('SESSION')) return 'primary';
  return 'default';
}
function statusBadge(eventType) {
  if (!eventType) return { label: 'diğer', color: 'dark' };

  const t = String(eventType);

  // ===== KRİTİK / ALARM =====
  if (
    [
      'PAYMENT_REJECTED',
      'PAYMENT_REQUIRED',
      'PAYMENT_DELAYED',
      'PACKAGE_OVERUSED',
      'MEMBER_INACTIVE_BY_DEBT',
    ].includes(t)
  ) {
    return { label: 'kritik', color: 'error' };
  }

  // ===== BORÇ =====
  if (t.startsWith('DEBT_')) {
    if (t === 'DEBT_CLEARED') return { label: 'kapatıldı', color: 'success' };
    if (['DEBT_CREATED', 'DEBT_ADDED'].includes(t)) return { label: 'borç', color: 'warning' };
    if (t === 'DEBT_REDUCED') return { label: 'azaltıldı', color: 'info' };
    return { label: 'borç', color: 'info' };
  }

  // ===== ÖDEME =====
  if (t.startsWith('PAYMENT_')) {
    if (t === 'PAYMENT_APPROVED') return { label: 'ödendi', color: 'success' };
    if (t === 'PAYMENT_CREATED') return { label: 'bekliyor', color: 'info' };
    if (t === 'PAYMENT_REJECTED') return { label: 'reddedildi', color: 'error' };
    if (t === 'PAYMENT_DELETED') return { label: 'silindi', color: 'dark' };
    return { label: 'ödeme', color: 'info' };
  }

  // ===== PAKET =====
  if (t.startsWith('PACKAGE_') || t === 'EARLY_TRANSFER_DONE') {
    if (['PACKAGE_ENDED', 'PACKAGE_FINISHED'].includes(t)) return { label: 'bitti', color: 'dark' };
    if (t === 'PACKAGE_ENDING_SOON') return { label: 'bitiyor', color: 'warning' };
    if (['PACKAGE_ACTIVATED', 'PACKAGE_RENEWED'].includes(t))
      return { label: 'aktif', color: 'success' };
    if (t === 'PACKAGE_DELETED') return { label: 'silindi', color: 'dark' };
    return { label: 'paket', color: 'info' };
  }

  // ===== YOKLAMA =====
  if (t.startsWith('ATTENDANCE_') || t.startsWith('MAKEUP_')) {
    if (t === 'ATTENDANCE_MARKED') return { label: 'katıldı', color: 'success' };
    if (t === 'ATTENDANCE_NO_SHOW') return { label: 'gelmedi', color: 'error' };
    if (t === 'ATTENDANCE_CANCELLED') return { label: 'iptal', color: 'dark' };
    if (t === 'ATTENDANCE_MAKEUP_USED') return { label: 'telafi', color: 'info' };
    return { label: 'yoklama', color: 'warning' };
  }

  // ===== SEANS =====
  if (t.startsWith('SESSION_')) {
    if (t === 'SESSION_DELETED') return { label: 'silindi', color: 'dark' };
    if (t === 'SESSION_PAYMENT_RECEIVED') return { label: 'ödendi', color: 'success' };
    if (t.includes('NOTE')) return { label: 'not', color: 'info' };
    if (t.includes('REMINDER')) return { label: 'hatırl.', color: 'info' };
    return { label: 'seans', color: 'primary' };
  }

  // ===== ÜYE =====
  if (t.startsWith('MEMBER_')) {
    if (t === 'MEMBER_CREATED') return { label: 'yeni', color: 'success' };
    if (t === 'MEMBER_STATUS_CHANGED') return { label: 'durum', color: 'info' };
    if (t === 'MEMBER_WARNING') return { label: 'uyarı', color: 'warning' };
    return { label: 'üye', color: 'info' };
  }

  // ===== PERSONEL =====
  if (t.startsWith('STAFF_')) {
    if (t.includes('PERMISSIONS')) return { label: 'yetki', color: 'warning' };
    if (t.includes('LOGIN')) return { label: 'giriş', color: 'info' };
    return { label: 'personel', color: 'primary' };
  }

  // ===== PLAN =====
  if (t.startsWith('PLAN_') || t.startsWith('MEMBER_PLAN_'))
    return { label: 'plan', color: 'info' };

  // ===== ACTIVITY =====
  if (t.startsWith('ACTIVITY_')) return { label: 'aktivite', color: 'info' };

  return { label: 'diğer', color: 'dark' };
}

// -------- component ----------
export function ActivityLogs() {
  const theme = useTheme();
  const now = useMemo(() => new Date(), []);

  const [filters, setFilters] = React.useState({
    period: 'TODAY',
    from: startOfDay(now),
    to: endOfDay(now),
    memberId: null,
    trainerId: null,
    memberQ: '',
    channel: 'UI', // ✅ ekle
  });

  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(25);
  const [total, setTotal] = React.useState(0);

  // local filters (client-side)
  const [searchText, setSearchText] = React.useState('');
  const [eventTypeFilter, setEventTypeFilter] = React.useState('ALL');

  const fetchLogs = useCallback(
    async (nextPage = 0, nextSize = size) => {
      setLoading(true);
      setErr(null);

      const payload = {
        from: toISODateInput(filters.from),
        to: toISODateInput(filters.to),
        trainerId: filters.trainerId,
        memberId: filters.memberId,
        channel: filters.channel || 'UI', // ✅ ekle
        page: nextPage,
        size: nextSize,
      };

      try {
        const res = await reportsApi.activityLogsByDate(payload);
        const items = res?.content ?? res?.items ?? [];
        const totalElements =
          res?.totalElements ?? res?.total ?? (Array.isArray(items) ? items.length : 0);

        setLogs(items);
        setTotal(Number(totalElements) || 0);
        setPage(nextPage);
        setSize(nextSize);
      } catch (e) {
        setErr(e);
        setLogs([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [filters, size]
  );

  useEffect(() => {
    fetchLogs(0, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    const d = new Date();
    setFilters({
      period: 'TODAY',
      from: startOfDay(d),
      to: endOfDay(d),
      memberId: null,
      trainerId: null,
      memberQ: '',
      channel: 'UI', // ✅ ekle
    });
    setSearchText('');
    setEventTypeFilter('ALL');
    setLogs([]);
    setTotal(0);
    setPage(0);
    setErr(null);
  };

  const eventTypeOptions = useMemo(() => {
    const uniq = Array.from(new Set((logs || []).map((x) => x?.eventType).filter(Boolean)));
    uniq.sort((a, b) => eventLabel(a).localeCompare(eventLabel(b), 'tr'));
    return ['ALL', ...uniq];
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const q = (searchText || '').trim().toLowerCase();
    const type = eventTypeFilter;

    return (logs || []).filter((l) => {
      if (type !== 'ALL' && l?.eventType !== type) return false;
      if (!q) return true;

      const hay = [l?.actorName, l?.description, l?.memberName, eventLabel(l?.eventType)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [logs, searchText, eventTypeFilter]);

  // ---- DataTable config ----
  const table = useMemo(() => {
    const EllipsisWithTooltip = ({ text, lines = 2, limit = 80 }) => {
      const t = (text ?? '').toString().trim();
      if (!t) return <span>—</span>;

      const isLong = t.length > limit;

      const content = (
        <span
          style={{
            display: '-webkit-box',
            WebkitLineClamp: lines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: isLong ? 'help' : 'default',
          }}
        >
          {t}
        </span>
      );

      // kısa metin → tooltip yok
      if (!isLong) return content;

      // uzun metin → tooltip var
      return (
        <Tooltip title={t} placement="top" arrow disableInteractive>
          {content}
        </Tooltip>
      );
    };
    // ✅ Projects örneği: Project cell (rounded avatar + name)
    const ProjectCell = ({ name, sub }) => (
      <MDBox display="flex" alignItems="center" lineHeight={1}>
        <MDAvatar name={name} size="sm" variant="rounded" bgColor="info">
          {initials(name || '')}
        </MDAvatar>

        <MDBox ml={1} lineHeight={1} sx={{ minWidth: 0 }}>
          <MDTypography display="block" variant="button" fontWeight="medium" noWrap>
            {name || '—'}
          </MDTypography>
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }} noWrap>
            {sub || '—'}
          </MDTypography>
        </MDBox>
      </MDBox>
    );

    // ✅ Projects örneği: Status text (chip yok)
    const StatusText = ({ text }) => (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {text || '—'}
      </MDTypography>
    );

    // ✅ Projects örneği: budget-like (button)
    const BudgetLike = ({ text, sub }) => (
      <MDBox lineHeight={1} sx={{ minWidth: 0, maxWidth: 520 }}>
        <MDTypography variant="button" color="text" fontWeight="medium" noWrap>
          {text || '—'}
        </MDTypography>

        <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
          <EllipsisWithTooltip text={sub} limit={120} maxWidth={520} />
        </MDTypography>
      </MDBox>
    );

    return {
      // ✅ Projects columns yapısı
      columns: [
        { Header: 'kullanıcı', accessor: 'user', width: '26%', align: 'left' },
        { Header: 'işlem', accessor: 'action', width: '44%', align: 'left' }, // ✅ sabitle
        { Header: 'status', accessor: 'status', width: '12%', align: 'center' },
        { Header: 'tarih', accessor: 'date', width: '14%', align: 'center' },
        { Header: '', accessor: 'more', width: '4%', align: 'center' },
      ],

      rows: (filteredLogs || []).map((l) => {
        const b = statusBadge(l?.eventType);

        return {
          user: <ProjectCell name={l?.actorName} sub={l?.memberName} />,
          action: <BudgetLike text={eventLabel(l?.eventType)} sub={l?.description} />,
          status: (
            <MDBox display="flex" justifyContent="center">
              <MDBadge badgeContent={b.label} color={b.color} variant="gradient" size="sm" />
            </MDBox>
          ),
          date: (
            <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
              {fmtTRDateTime(l?.createdAt)}
            </MDTypography>
          ),
          more: (
            <MDTypography
              component="a"
              href="#"
              color="text"
              onClick={(e) => {
                e.preventDefault();
                console.log('detail', l);
              }}
              sx={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <Icon>more_vert</Icon>
            </MDTypography>
          ),
        };
      }),
    };
  }, [filteredLogs]);

  const shownCount = filteredLogs.length;
  const totalCount = total > 0 ? total : logs.length;

  return (
    <MDBox>
      {/* Header */}
      <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1.25}></MDBox>

      {/* Server Filters */}
      <ActivityLogsFiltersBar
        value={filters}
        onChange={setFilters}
        listing={loading}
        onReset={reset}
        onList={() => fetchLogs(0, size)}
      />

      {/* Top mini toolbar */}
      <MDBox
        mt={2}
        mb={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDTypography variant="button" fontWeight="medium">
            Log Tablosu
          </MDTypography>
          {(eventTypeFilter !== 'ALL' || searchText.trim()) && (
            <Chip
              size="small"
              label="Filtre aktif"
              color="info"
              variant="outlined"
              onDelete={() => {
                setSearchText('');
                setEventTypeFilter('ALL');
              }}
            />
          )}
        </MDBox>

        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <MDInput
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Ara: kullanıcı, açıklama, üye, tür…"
            icon={{ component: 'search', direction: 'left' }}
            sx={{ width: { xs: '100%', sm: 340 } }}
          />

          <Autocomplete
            size="small"
            options={eventTypeOptions}
            value={eventTypeFilter}
            onChange={(e, v) => setEventTypeFilter(v || 'ALL')}
            getOptionLabel={(v) => (v === 'ALL' ? 'Tümü' : eventLabel(v))}
            isOptionEqualToValue={(o, v) => o === v}
            renderInput={(params) => (
              <TextField
                {...params}
                label="İşlem Türü"
                placeholder="Tümü"
                sx={{
                  minWidth: 240,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
              />
            )}
          />
        </MDBox>
      </MDBox>

      {/* DataTable */}
      <DataTable
        table={table}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        noEndBorder
        canSearch={false} // biz üstte kendi search yaptık
      />

      {/* errors / loading */}
      {err ? (
        <MDBox mt={1}>
          <MDTypography variant="button" color="error">
            Loglar alınamadı.
          </MDTypography>
        </MDBox>
      ) : null}

      {loading ? (
        <MDBox mt={1} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <MDTypography variant="button" sx={{ opacity: 0.75 }}>
            Yükleniyor...
          </MDTypography>
        </MDBox>
      ) : null}
      <Chip
        size="small"
        variant="outlined"
        label={loading ? 'Yükleniyor…' : `${shownCount}/${totalCount} kayıt`}
        sx={{ fontWeight: 700 }}
      />
      {/* Pagination (server-side) */}
      <MDBox mt={1}>
        <TablePagination
          component="div"
          count={total || 0}
          page={page}
          onPageChange={(e, nextPage) => fetchLogs(nextPage, size)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            fetchLogs(0, next);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </MDBox>
    </MDBox>
  );
}
